<template>
  <div
    :class="notificationClasses"
    class="notification-item-wrapper"
    @click="handleClick"
  >
    <!-- Unread indicator bar -->
    <div v-if="!notification.read && !selectionMode" class="unread-bar" />

    <!-- Selection checkbox (shown in selection mode) -->
    <div v-if="selectionMode" class="selection-checkbox" @click.stop>
      <v-checkbox
        :model-value="selected"
        hide-details
        density="compact"
        color="primary"
        @update:model-value="$emit('update:selected', Boolean($event))"
      />
    </div>

    <!-- Actions menu - fixed top right (hidden in selection mode) -->
    <div v-if="!selectionMode" class="notification-actions">
      <v-btn
        v-if="!notification.read"
        icon
        variant="text"
        size="x-small"
        density="comfortable"
        class="action-btn"
        @click.stop="$emit('mark-read', notification.id)"
      >
        <v-icon icon="mdi-email-open" size="16" />
        <v-tooltip activator="parent" location="bottom">Mark as read</v-tooltip>
      </v-btn>

      <v-menu location="bottom end">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            icon
            variant="text"
            size="x-small"
            density="comfortable"
            class="action-btn"
            @click.stop
          >
            <v-icon icon="mdi-dots-horizontal" size="16" />
          </v-btn>
        </template>

        <v-list density="compact" min-width="160">
          <v-list-item
            v-if="!notification.read"
            @click="$emit('mark-read', notification.id)"
          >
            <template #prepend>
              <v-icon icon="mdi-email-open" size="18" />
            </template>
            <v-list-item-title>Mark as read</v-list-item-title>
          </v-list-item>

          <v-divider v-if="!notification.read" />

          <v-list-item @click="$emit('remove', notification.id)" class="text-error">
            <template #prepend>
              <v-icon icon="mdi-delete" color="error" size="18" />
            </template>
            <v-list-item-title>Delete</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <!-- Main content area - PostCard style layout -->
    <div class="notification-content-area">
      <!-- Header row with avatar and user info -->
      <div class="notification-header-row">
        <!-- Avatar -->
        <v-avatar :size="36" class="notification-avatar">
          <v-img
            v-if="actorAvatar"
            :src="actorAvatar"
            :alt="actorName"
            cover
          />
          <div v-else class="notification-icon-wrapper" :style="{ backgroundColor: typeColorRgb }">
            <v-icon :icon="typeIcon" color="white" size="18" />
          </div>
        </v-avatar>

        <!-- User info header -->
        <UserNameDisplay
          :display-name="actorName"
          :username="actorUsername"
          :verified="notification.data?.actorVerified"
          :time="notification.createdAt"
          class="notification-header"
        />
      </div>

      <!-- Message content - indented to align with header text -->
      <div class="notification-body">
        <!-- Primary message -->
        <div v-if="notification.message" class="notification-message">
          {{ notification.message }}
        </div>

        <!-- Fallback: If no message, show action text -->
        <div v-else-if="actionText" class="notification-message">
          {{ actionText }}
        </div>

        <!-- Song preview (enhanced layout for song notifications) -->
        <div
          v-if="isSongNotification && (postImage || notification.postTitle)"
          class="song-preview"
        >
          <div class="song-preview__artwork">
            <v-img
              v-if="postImage"
              :src="postImage"
              :alt="notification.postTitle || 'Song'"
              class="song-preview__image"
              cover
            />
            <div v-else class="song-preview__placeholder">
              <v-icon icon="mdi-music" size="20" />
            </div>
            <div class="song-preview__play-overlay">
              <v-icon icon="mdi-play" size="16" color="white" />
            </div>
          </div>
          <div class="song-preview__info">
            <span class="song-preview__title">{{ notification.postTitle || 'Untitled Song' }}</span>
            <span v-if="songArtist" class="song-preview__artist">{{ songArtist }}</span>
          </div>
        </div>

        <!-- Post preview (for non-song notifications) -->
        <div
          v-else-if="!isSongNotification && (notification.postTitle || notification.data.postContent) &&
                notification.message !== (notification.postTitle || notification.data.postContent)"
          class="post-preview"
        >
          <v-img
            v-if="postImage"
            :src="postImage"
            :alt="notification.postTitle || 'Post'"
            class="post-preview-image"
            cover
          />
          <span class="post-preview-text">{{ truncate(notification.postTitle || notification.data.postContent || '', 100) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import type { Notification } from '@/types/notification';
import { useSettingsStore } from '@/stores/settings';
import UserNameDisplay from '@/components/common/UserNameDisplay.vue';

interface Props {
  notification: Notification;
  selectionMode?: boolean;
  selected?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  selectionMode: false,
  selected: false,
});

const emit = defineEmits<{
  click: [notification: Notification];
  'mark-read': [notificationId: string];
  remove: [notificationId: string];
  'follow-back': [notification: Notification];
  'update:selected': [value: boolean];
}>();

const settingsStore = useSettingsStore();

// Initialize settings on mount if not already initialized
onMounted(() => {
  if (!settingsStore.isInitialized) {
    settingsStore.initialize().catch((err: Error) => {
      console.error('Failed to initialize settings:', err);
    });
  }
});

const notificationClasses = computed(() => ({
  'notification-item': true,
  'notification-item--unread': !props.notification.read,
  'notification-item--read': props.notification.read,
  'notification-item--selection-mode': props.selectionMode,
  'notification-item--selected': props.selected,
}));

function handleClick() {
  if (props.selectionMode) {
    // In selection mode, toggle selection
    emit('update:selected', !props.selected);
  } else {
    // Normal mode, emit click
    emit('click', props.notification);
  }
}

const actorAvatar = computed(() => {
  // For system notifications without a user, use site logo from branding
  if (!props.notification.fromUserId && !props.notification.data?.actorAvatar) {
    return settingsStore.getSiteLogo();
  }

  // Always show the actor's avatar (the person who triggered the notification)
  // Song cover art is shown in the post preview section, not here
  return props.notification.fromUserAvatar || props.notification.data?.actorAvatar;
});

const actorName = computed(() => {
  // For system notifications (no fromUserId), show app name from branding
  if (!props.notification.fromUserId && !props.notification.data?.actorName) {
    return settingsStore.getSiteName();
  }
  return props.notification.fromUserName || props.notification.data?.actorName || 'Someone';
});

const actorUsername = computed(() => {
  // For system notifications, don't show username
  if (!props.notification.fromUserId) {
    return null;
  }
  return props.notification.fromUserUsername || props.notification.data?.actorUsername || null;
});

const postImage = computed(() => {
  // Get the post/song cover image for the preview
  return props.notification.postImage || props.notification.data?.postImage || null;
});

const isSongNotification = computed(() => {
  const type = props.notification.type;
  const notificationType = props.notification.notificationType;
  const postType = props.notification.postType || props.notification.data?.postType;

  return (
    type === 'song_upload_succeeded' ||
    notificationType === 'song_upload_succeeded' ||
    postType === 'SONG' ||
    (type === 'rating' && postType === 'SONG') ||
    (type === 'like' && postType === 'SONG') ||
    notificationType === 'post_approved' && postType === 'SONG'
  );
});

const songArtist = computed(() => {
  // Try to get artist from notification data
  return props.notification.data?.postArtist ||
         props.notification.data?.artist ||
         props.notification.fromUserName ||
         null;
});

const actionText = computed(() => {
  const type = props.notification.type;
  const content = props.notification.content || props.notification.data;

  // Handle post_rejected with reason
  if (type === 'post_rejected') {
    const groupName = content?.group_name || 'the group';
    const reason = content?.reason;
    if (reason) {
      return `Your post in ${groupName} was rejected: "${reason}"`;
    }
    return `Your post in ${groupName} was rejected`;
  }

  // Handle post_approved
  if (type === 'post_approved') {
    const groupName = content?.group_name || 'the group';
    return `Your post in ${groupName} was approved`;
  }

  // Handle content_removed (moderation)
  if (type === 'content_removed') {
    const contentType = content?.content_type || 'post';
    const reason = content?.reason || 'policy violation';
    const postPreview = props.notification.postTitle || props.notification.data?.postContent;
    const preview = postPreview ? `"${truncate(postPreview, 50)}"` : `Your ${contentType}`;
    return `${preview} has been removed for the following policy violation: ${reason}`;
  }

  // Handle account_warning (moderation)
  if (type === 'account_warning') {
    const reason = content?.reason || 'policy violation';
    return `You have received a warning for: ${reason}`;
  }

  // Handle account_banned (moderation)
  if (type === 'account_banned') {
    const reason = content?.reason || 'policy violations';
    return `Your account has been suspended for: ${reason}`;
  }

  const actions: Record<string, string> = {
    like: 'liked your post',
    reaction: 'reacted to your post',
    repost: 'reposted your post',
    comment: 'commented on your post',
    mention: 'mentioned you',
    follow: 'started following you',
    follow_request: 'requested to follow you',
    follow_accepted: 'accepted your follow request',
    message: 'sent you a message',
    rating: 'rated your post',
    system: '',
  };
  return actions[type] || 'interacted with your content';
});

const typeIcon = computed(() => {
  const icons: Record<string, string> = {
    like: 'mdi-heart',
    reaction: 'mdi-emoticon-happy',
    repost: 'mdi-repeat',
    comment: 'mdi-comment',
    mention: 'mdi-at',
    follow: 'mdi-account-plus',
    follow_request: 'mdi-account-question',
    follow_accepted: 'mdi-account-check',
    message: 'mdi-message',
    rating: 'mdi-star',
    system: 'mdi-bell',
    group_invite: 'mdi-account-group',
    post_approved: 'mdi-check-circle',
    post_rejected: 'mdi-close-circle',
    content_removed: 'mdi-delete-alert',
    account_warning: 'mdi-alert',
    account_banned: 'mdi-account-cancel',
  };
  return icons[props.notification.type] || 'mdi-bell';
});

const typeColorRgb = computed(() => {
  const colorMap: Record<string, string> = {
    like: 'rgb(var(--v-theme-error))',
    reaction: 'rgb(var(--v-theme-warning))',
    repost: 'rgb(var(--v-theme-success))',
    comment: 'rgb(var(--v-theme-info))',
    mention: 'rgb(var(--v-theme-warning))',
    follow: 'rgb(var(--v-theme-primary))',
    follow_request: 'rgb(var(--v-theme-primary))',
    follow_accepted: 'rgb(var(--v-theme-success))',
    message: 'rgba(var(--v-theme-on-surface), 0.6)',
    rating: 'rgb(var(--v-theme-warning))',
    system: 'rgb(var(--v-theme-info))',
    group_invite: 'rgb(var(--v-theme-info))',
    post_approved: 'rgb(var(--v-theme-success))',
    post_rejected: 'rgb(var(--v-theme-error))',
    content_removed: 'rgb(var(--v-theme-error))',
    account_warning: 'rgb(var(--v-theme-warning))',
    account_banned: 'rgb(var(--v-theme-error))',
  };
  return colorMap[props.notification.type] || 'rgb(var(--v-theme-primary))';
});

function truncate(text: string, length: number): string {
  if (!text) return '';
  return text.length > length ? text.substring(0, length) + '...' : text;
}
</script>

<style scoped>
/* Main wrapper with modern styling */
.notification-item-wrapper {
  position: relative;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  background-color: rgb(var(--v-theme-surface));
  border-bottom: 1px solid rgba(var(--v-border-color), 0.5);
  overflow: hidden;
}

.notification-item-wrapper:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.03);
}

.notification-item-wrapper.notification-item--unread {
  background-color: transparent;
}

.notification-item-wrapper.notification-item--unread:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.03);
}

/* Read notifications at 50% opacity */
.notification-item-wrapper.notification-item--read {
  opacity: 0.5;
}

.notification-item-wrapper.notification-item--read:hover {
  opacity: 0.7;
}

/* Unread indicator bar */
.unread-bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(
    180deg,
    rgb(var(--v-theme-primary)) 0%,
    rgba(var(--v-theme-primary), 0.7) 100%
  );
  animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideIn {
  from {
    transform: scaleY(0);
    transform-origin: top;
  }
  to {
    transform: scaleY(1);
  }
}

/* Actions menu - fixed top right */
.notification-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
}

.notification-item-wrapper:hover .notification-actions {
  opacity: 1;
}

.action-btn {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.action-btn:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.08);
}

/* Selection mode styles */
.selection-checkbox {
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
}

.notification-item--selection-mode .notification-content-area {
  padding-left: 48px;
}

.notification-item--selected {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

.notification-item--selected:hover {
  background-color: rgba(var(--v-theme-primary), 0.12);
}

/* Content area layout */
.notification-content-area {
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  padding-right: 36px; /* Space for actions menu */
}

/* Header row with avatar and user info */
.notification-header-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

/* Avatar */
.notification-avatar {
  flex-shrink: 0;
}

.notification-icon-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

/* Header with user info */
.notification-header {
  flex: 1;
  min-width: 0;
}

/* Body content - full width starting from left */
.notification-body {
  margin-top: 2px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.notification-message {
  color: rgba(var(--v-theme-on-surface), 0.9);
  font-size: 0.875rem;
  line-height: 1.35;
}

/* Post preview */
.post-preview {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background-color: rgba(var(--v-theme-on-surface), 0.03);
  border-radius: 8px;
  border-left: 3px solid rgba(var(--v-theme-primary), 0.3);
}

.post-preview-image {
  width: 40px !important;
  min-width: 40px !important;
  max-width: 40px !important;
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  border-radius: 6px;
  flex-shrink: 0;
  overflow: hidden;
}

.post-preview-image :deep(.v-img__img) {
  object-fit: cover;
}

.post-preview-text {
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-size: 0.875rem;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* Song preview - enhanced layout for music notifications */
.song-preview {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: linear-gradient(135deg, rgba(88, 28, 135, 0.8) 0%, rgba(49, 46, 129, 0.6) 100%);
  border-radius: 10px;
  margin-top: 6px;
}

.song-preview__artwork {
  position: relative;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
}

.song-preview__image {
  width: 100% !important;
  height: 100% !important;
}

.song-preview__image :deep(.v-img__img) {
  object-fit: cover;
}

.song-preview__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}

.song-preview__play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
}

.song-preview__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.song-preview__title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.song-preview__artist {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Mobile optimizations */
@media (max-width: 600px) {
  .notification-content-area {
    padding: 10px 12px;
    padding-right: 36px;
  }

  .notification-header-row {
    gap: 8px;
  }

  .notification-actions {
    opacity: 0.7;
  }

  .notification-item-wrapper:hover .notification-actions {
    opacity: 1;
  }
}

/* Accessibility */
.notification-item-wrapper:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: -2px;
}

/* Dark mode adjustments */
@media (prefers-color-scheme: dark) {
  .notification-avatar {
    border-color: rgba(var(--v-border-color), 0.4);
  }

  .post-preview {
    background-color: rgba(var(--v-theme-on-surface), 0.05);
  }
}
</style>
